import React from 'react';
import { Badge, PhoneNumber, kitCopy, phoneRelationLabel, phone as phoneLib, UNKNOWN_RELATION } from '@parsina/framework';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useReceptionStrings } from '../lib/receptionStrings';
import { matchedOtherPhone, patientPhones, phoneValue } from '../lib/patientPhones';

// Unicode isolates: a number inside a plain-text `title` reads as one LTR run, the
// way the LTR island keeps it on screen (DESIGN_SYSTEM §6.4).
const LRI = '⁦';
const PDI = '⁩';

/**
 * A patient's main number where a row shows a phone (PM-0536): the kit's
 * `PhoneNumber` (the page's digits, an LTR island) and «+N» when the record holds
 * more — the tags-overflow precedent, an outline badge whose `title` lists the others
 * with whose number each is, and whose meaning a screen reader hears in words.
 *
 * With `query` (a search's text) the row shows the number the search matched when
 * that was not the main one, with «یافته با شمارهٔ دیگر» and whose number it is.
 *
 * Renders nothing for a patient with no number: each host keeps its own empty state.
 * `badgeStyle` lets a host on a coloured surface keep the badge in its own ink.
 */
export default function PatientPhones({ patient, query, isMobile, badgeStyle, 'data-testid': testid = 'patient-phones' }) {
  const s = useReceptionStrings();
  const { locale, formatNumber, formatDigits } = useTranslation();
  const all = patientPhones(patient);
  if (!all.length) return null;

  const matched = query ? matchedOtherPhone(patient, query) : null;
  const shown = matched || all[0];
  const rest = all.filter((row) => row !== shown);
  // «نامشخص» is every number from before PM-0536: saying it adds nothing.
  const whose = (row) => (row.relation && row.relation !== UNKNOWN_RELATION ? phoneRelationLabel(row.relation, locale) : '');
  const titleOf = (row) => {
    const number = `${LRI}${formatDigits(phoneLib.forDisplay(phoneValue(row)))}${PDI}`;
    const relation = whose(row);
    return relation ? s('dashboard.phones.otherLine', { phone: number, relation }) : number;
  };

  return (
    <span
      data-testid={testid}
      data-matched={matched ? 'other' : undefined}
      style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, minWidth: 0, maxWidth: '100%' }}
    >
      <PhoneNumber value={phoneValue(shown)} locale={locale} isMobile={isMobile} data-testid={`${testid}-number`} />
      {matched && (
        // Separate spans, never a joined string: a literal separator would be copy
        // outside the catalogues.
        <span data-testid={`${testid}-matched`} style={{ display: 'inline-flex', gap: 6 }}>
          {[kitCopy(locale).phoneCandidates.matchedOther, whose(matched)].filter(Boolean)
            .map((part) => <span key={part}>{part}</span>)}
        </span>
      )}
      {rest.length > 0 && (
        <Badge
          variant="outline"
          // One number per line: a «·» beside Persian digits reads as a zero.
          title={rest.map(titleOf).join('\n')}
          style={badgeStyle}
          data-testid={`${testid}-more`}
        >
          <span aria-hidden="true">+{formatNumber(rest.length)}</span>
          <span className="sr-only">{s('dashboard.phones.more', { count: formatNumber(rest.length) })}</span>
        </Badge>
      )}
    </span>
  );
}
